import React from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { formatPartnerSource } from '../../../helpers/PartnerHelper';

const getEvaluationClassByKey = (classes, key) => {
  const mapping = {
    good: classes.badgeEvalGood,
    average: classes.badgeEvalAverage,
    poor: classes.badgeEvalPoor,
    acceptable: classes.badgeEvalAcceptable,
    missing: classes.badgeMissing,
  };
  return mapping[key] || classes.badgeMissing;
};

const getSituationClass = (classes, situation) => {
  if (situation === 'EN COURS') return classes.badgeSituationEncours;
  if (situation === 'RESILIE') return classes.badgeSituationResilie;
  return classes.badgeSituationFin;
};

const getHistoryColor = (key) => {
  const mapping = {
    good: '#16a34a',
    average: '#d97706',
    poor: '#dc2626',
    acceptable: '#2563eb',
    missing: '#64748b',
  };
  return mapping[key] || '#64748b';
};

const EvalutionFiche = ({
  rows = [],
  classes,
  formatDate,
  getEvaluationMeta,
  onOpenFiche,
}) => {
  if (!rows.length) {
    return <Alert severity="info">Aucun resultat.</Alert>;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 1.25,
      }}
    >
      {rows.map((row) => {
        const evaluationMeta = getEvaluationMeta(row.evaluation);
        const evaluationClass = getEvaluationClassByKey(classes, evaluationMeta.key);
        const situationClass = getSituationClass(classes, row.situation);
        const historyRows = Array.isArray(row.evalHistory) ? row.evalHistory : [];

        return (
          <Paper
            key={row.id}
            elevation={0}
            sx={{
              p: 1.5,
              border: '0.8px solid #dbe6f3',
              borderRadius: 1.5,
              transition: 'all .2s',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                borderColor: '#c9dbef',
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1f2a44' }}>
              {row.partenaire}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
              {row.directions || '-'}
            </Typography>
            {row.readOnly ? <Chip label="Lecture seule" size="small" sx={{ mb: 1, bgcolor: '#eef2f6', color: '#475467', fontWeight: 700 }} /> : null}

            <Stack spacing={0.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Situation
                </Typography>
                <span className={`${classes.badge} ${situationClass}`}>{row.situation || '-'}</span>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Evaluation
                </Typography>
                <span className={`${classes.badge} ${evaluationClass}`}>{evaluationMeta.label}</span>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Date eval.
                </Typography>
                <Typography variant="caption" className={classes.monoCell}>
                  {formatDate(row.evaluationDate)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Marchés
                </Typography>
                <Typography variant="caption" className={classes.monoCell}>
                  {row.nbMarches || 0}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>Base source</Typography>
                <Typography variant="caption" sx={{ color: '#173d61', fontWeight: 700, textAlign: 'right' }}>
                  {formatPartnerSource(row.sourceDb)}
                </Typography>
              </Box>
            </Stack>

            {historyRows.length > 0 ? (
              <Box sx={{ mt: 1, pt: 1, borderTop: '0.8px solid #e6edf5' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                  Historique
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {historyRows.map((history, index) => {
                    const historyMeta = getEvaluationMeta(history.result);
                    return (
                      <Box key={`${history.date || 'no-date'}-${history.result}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: getHistoryColor(historyMeta.key),
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="caption" className={classes.monoCell} sx={{ minWidth: 72 }}>
                          {formatDate(history.date)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', flex: 1 }}>
                          {history.result}
                        </Typography>
                        {index === 0 ? (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 9,
                              fontWeight: 700,
                              px: 0.75,
                              borderRadius: 1,
                              color: '#fff',
                              backgroundColor: '#2563eb',
                            }}
                          >
                            DERNIERE
                          </Typography>
                        ) : null}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ) : null}

            <Box sx={{ mt: 1.25 }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => onOpenFiche?.(row)}
                sx={{ textTransform: 'none', borderRadius: 1.25, fontWeight: 600 }}
              >
                Fiche
              </Button>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default EvalutionFiche;
